import { format, startOfYesterday } from 'date-fns';
import { OrgMetric, UsageLimit } from 'modules/domains/billing';
import { useMemo } from 'react';
import { sumObjectListByKey } from 'utils/arrayHelpers';
import { SMS_METRICS } from '../constants';
import { useDateContext } from '../contexts/DateContext';
import { useQueryContext } from '../contexts/QueryContext';
import { useUsageLimitsContext } from '../contexts/UsageLimitsContext';
import { createAsOfDateText, createDateRangeText, getMinDate } from '../utils';

const defaultMetricInfo = { dateRangeText: '', lastAvailableDateText: '', value: 0 };
const yesterday = format(startOfYesterday(), 'yyyy-MM-dd');

/** Get the annual usage limit for a given metric, with any other business logic applied */
const getAnnualLimit = (metricName: string, metricUsageLimit: Record<string, UsageLimit[]>) => {
  const metricLimits = metricUsageLimit[metricName] || [];
  const filtered = metricLimits?.filter(
    (limit) =>
      metricName !== SMS_METRICS.smsSegmentsSent ||
      !limit.attributes.RecipientCountryName ||
      limit.attributes.RecipientCountryName === 'United States',
  );

  return filtered.length > 0 ? sumObjectListByKey(filtered, 'annualLimit') : undefined;
};

type UseMetricInfoResult = {
  annualLimit?: number;
  /** Date range for metric value shown, localized such as May 10 - June 9, 2025 */
  dateRangeText: string;
  /** Date of the high watermark value, only available on specific metrics */
  highWatermarkDateText?: string;
  /** Last date that metrics were provided, varies based on period selected and dates provided by API response */
  lastAvailableDateText: string;
  /** Metric value from beginning of billing cycle (contract) and up to last available date */
  totalValue?: number;
  /** Metric value for the date range selected */
  value: number;
};

/**
 * Returns dynamic data related to an OrgMetric in order to calculate the value
 * and date range related to the value
 */
export const useMetricInfo = (
  metricNames: string[],
  orgMetricsByName?: Record<string, OrgMetric>,
): UseMetricInfoResult[] => {
  const { metricUsageLimit } = useUsageLimitsContext();
  const {
    isLoading: isLoadingQuery,
    lastAvailableDatePST = yesterday,
    orgMetricsByName: queryOrgMetricsByName,
    billingCycleMetrics,
  } = useQueryContext();
  const { billingPeriod, isLoading: isLoadingDates } = useDateContext();
  const isLoading = isLoadingDates || isLoadingQuery;
  const metricsByName = orgMetricsByName ?? queryOrgMetricsByName;

  const metricInfo = useMemo(() => {
    return isLoading
      ? metricNames.map(() => defaultMetricInfo)
      : metricNames.map((metricName) => {
          // get current metric to find last available date and high watermark date
          const currentMetric = metricsByName[metricName];
          const annualLimit = getAnnualLimit(metricName, metricUsageLimit);

          // get latest date from metadata or the selected date range
          const lastAvailable = getMinDate([
            // use metadata if available, otherwise use the last available date from the API response
            currentMetric?.metadata?.lastAvailableDate ?? lastAvailableDatePST,
            // use endDate if it is before the last available date above
            billingPeriod.endDatePST,
          ]);

          return {
            annualLimit,
            dateRangeText: createDateRangeText(billingPeriod.startDatePST, lastAvailable),
            highWatermarkDateText: createAsOfDateText(
              currentMetric?.metadata?.highWatermarkDate || '',
            ),
            lastAvailableDateText: createAsOfDateText(lastAvailable),
            totalValue: billingCycleMetrics[metricName]?.value,
            value: currentMetric?.value || 0,
          };
        });
  }, [
    billingPeriod,
    isLoading,
    lastAvailableDatePST,
    metricNames,
    metricsByName,
    metricUsageLimit,
    billingCycleMetrics,
  ]);

  return metricInfo;
};
